import type { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { AudioPlayer, type MediaPlayer } from '@/components/audio-player.tsx';
import { NavBack } from '@/components/nav-back.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { YouTubePlayer } from '@/components/youtube-player.tsx';
import { appNotify, onAppEvent } from '@/lib/app-bridge.ts';
import { MSG } from '@/lib/constants.ts';
import {
  claimReward,
  prepareCampaign,
  reportPlayback,
  type PlaybackResult,
} from '@/lib/campaign.api.ts';
import { campaignThumbnail, formatMinSec, returnUrl } from '@/lib/utils.ts';
import { benefitUntil, missionBoosted, missionRepeat, missionReward } from '@/data/mission.ts';
import { extractYoutubeId, YOUTUBE_ERROR } from '@/lib/youtube.ts';
import { askAlert, askConfirm, askCustom } from '@/store/dialog.ts';
import { useLeaveConfirm } from '@/hooks/use-leave-confirm.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { DaoCampaign, DaoReward } from '@/types/dao.ts';
import type { PageProps } from '@/types/props.ts';

/** 서버 검증식이 5초 간격을 전제한다 (campaign.api.ts 참고) */
const REPORT_INTERVAL_SEC = 5;

/**
 * "언제 다시 올 수 있는지" 를 사람 말로.
 *
 * 재참여 주기가 미션마다 다르다(`F_INIT_TIME`). 예전에는 화면이 무조건 "내일 다시
 * 시도해 주세요" 라고 했는데, 12시간마다인 미션에서는 사실과 달랐다.
 *
 * 서버가 계산한 시각(`nextRewardAt`)을 받아 남은 시간에 맞춰 말한다.
 * 시각을 못 받았으면(옛 서버 등) 두루뭉술하게라도 틀리지 않는 문장을 쓴다.
 *
 * @param at `Y-m-d H:i:s` — 서버 시각 기준
 */
function retryText(at?: string | null): string {
  if (!at) return '잠시 후 다시 시도해 주세요.';

  // 'Y-m-d H:i:s' 를 그대로 Date 에 넣으면 사파리가 NaN 을 낸다. 하이픈을 슬래시로.
  const next = new Date(at.replace(/-/g, '/'));
  if (Number.isNaN(next.getTime())) return '잠시 후 다시 시도해 주세요.';

  const left = next.getTime() - Date.now();
  if (left <= 0) return '잠시 후 다시 시도해 주세요.';

  const minutes = Math.ceil(left / 60000);
  if (minutes < 60) return `약 ${minutes}분 뒤에 다시 참여할 수 있어요.`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    /*
      ★ 한 시간이 넘으면 **시간만** 말한다. (2026-09-16 요청)
        '약 6시간 23분' 은 정확해 보이지만, 읽는 사람에게는 '약 6시간' 과 같은 정보다.

      ★ **반올림한다.** 내림으로 두면 1시간 59분 남았는데 '약 1시간' 이라고 말해서,
        한 시간 뒤에 다시 왔다가 또 거절당한다. 안내와 실제가 어긋나는 것은
        (`nextRewardAt` 의 자정 문제와 같은) 가장 피해야 할 종류의 오류다.
    */
    return `약 ${Math.round(minutes / 60)}시간 뒤에 다시 참여할 수 있어요.`;
  }

  // 하루가 넘으면 시간보다 날짜가 알아보기 쉽다.
  const date = `${next.getMonth() + 1}월 ${next.getDate()}일`;
  const hour = next.getHours();
  const clock = hour < 12 ? `오전 ${hour === 0 ? 12 : hour}시` : `오후 ${hour === 12 ? 12 : hour - 12}시`;
  return `${date} ${clock} 이후에 다시 참여할 수 있어요.`;
}

/**
 * 유튜브로 재생하는가.
 *
 * ★ **유튜브 주소가 있으면 유튜브다 — 음악 미션도.** (2026-09-15 변경)
 *   음악도 유튜브로 트래픽을 보내야 한다는 요청이다. 주소가 없는 음악 미션만 mp3 로
 *   재생한다. 서버도 같은 규칙이다 — 재생시간을 유튜브 길이로 저장하고
 *   (`ControllerAdmin::autoFillSong`), 음원 티켓은 mp3 로 재생할 때만 준다(`prepare`).
 */
function playsYoutube(campaign: DaoCampaign): boolean {
  return campaign.type !== 'M' || !!extractYoutubeId(campaign.youtubeUrl);
}

/**
 * 이 화면을 빠져나갈 곳.
 *
 * '오늘은 이미 받았어요' 같은 안내를 띄운 뒤 어디로 보낼지 정한다. 그냥 직전
 * 경로로 되돌리면 곤란한 경우가 있다.
 *
 *   직전이 없음        새 탭·공유 링크로 바로 들어온 경우. 돌아갈 곳이 없다.
 *   직전이 `/login`    로그인을 거쳐 들어왔다. 되돌리면 로그인 화면으로 튕긴다.
 *   직전이 `/play/...` 다른 재생 화면이다. 거기서도 같은 안내가 날 수 있어 맴돈다.
 *
 * 셋 다 **메인으로 보낸다.**
 */
function exitPath(previous: string | null): string {
  if (!previous) return '/';
  if (previous.startsWith('/login')) return '/';
  if (previous.startsWith('/play/')) return '/';
  return previous;
}

/**
 * 미션 재생 보상 (시안 play.html).
 *
 * **재생기는 유튜브 주소가 정한다** (`playsYoutube()`).
 *   주소 있음        — 유튜브 IFrame 플레이어 (영상 미션, 주소가 있는 음악 미션)
 *   음악 · 주소 없음 — 서버에서 바이트로 받아 Blob 으로 재생하는 오디오 플레이어
 *
 * 보고 방식은 둘이 같다. 5초마다 서버에 진행 상황을 보내고, 서버가 그 기록으로
 * 실제 재생 여부를 판정해 포인트를 적립한다. 두 재생기 모두 `getCurrentTime()` /
 * `getDuration()` 을 주므로(`MediaPlayer`) 보고 코드는 하나로 둔다.
 */
const Play: FunctionalComponent<PageProps<{ id?: string }>> = ({ id }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const hydrated = useAppSelector((state) => state.auth.hydrated);
  const previousPath = useAppSelector((state) => state.ui.previousPath);

  const [campaign, setCampaign] = useState<DaoCampaign>();
  const [videoId, setVideoId] = useState<string>();
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [claimed, setClaimed] = useState(false);
  /**
   * '적립 받기' 를 누를 수 있는가. **서버가 보고마다 알려 준다.**
   *
   * 화면이 스스로 초를 세지 않는다. 적립 판정은 서버의 보고 기록으로만 하므로,
   * 화면이 따로 셈하면 버튼은 열렸는데 눌러도 안 되는 일이 생긴다.
   */
  const [claimable, setClaimable] = useState(false);
  /** 적립 요청을 보내는 중 (버튼 연타 방지) */
  const [claiming, setClaiming] = useState(false);
  /** 음원을 받을 때 쓰는 한 번짜리 티켓 (`prepare` 가 준다) */
  const [audioTicket, setAudioTicket] = useState<string | null>(null);
  /** 총예산이 바닥난 미션. 들을 수는 있지만 적립은 되지 않는다. */
  const [soldOut, setSoldOut] = useState(false);

  /** 화면 안의 링크 클릭을 한 곳에서 가로채기 위한 컨테이너 */
  const pageRef = useRef<HTMLDivElement>(null);
  // 유튜브 플레이어와 오디오 플레이어 둘 다 들어온다.
  const playerRef = useRef<MediaPlayer | null>(null);
  const rewardRef = useRef<DaoReward | undefined>(undefined);
  const playingRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  /** 종료 보고를 두 번 보내지 않기 위한 잠금 */
  const finishedRef = useRef(false);
  /**
   * 마지막으로 보고한 **재생 위치**(초).
   *
   * 벽시계가 아니라 위치를 기준으로 보고해야 한다. 일시정지하면 위치가 멈추므로
   * 보고도 저절로 멈춘다 — 서버 검증이 전제하는 동작이다.
   */
  const reportedAtRef = useRef(0);

  const backTo = exitPath(previousPath);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * 서버가 이 재생을 끊었다. 멈추고 알린다.
   *
   * ★ **취소 보고를 다시 보내지 않는다.** 서버는 이미 이 재생을 끝낸 상태라
   *   더 보낼 것이 없고, 보내 봐야 같은 오류만 돌아온다. `finishedRef` 를 세워
   *   화면을 떠날 때의 정리도 건너뛰게 한다.
   */
  const abort = useCallback(
    (message: string) => {
      if (finishedRef.current) return;
      finishedRef.current = true;

      clearTimer();
      setPlaying(false);
      playingRef.current = false;
      // 소리를 실제로 멈춘다. 이게 없으면 적립되지 않는 줄 모르고 끝까지 듣는다.
      playerRef.current?.pause();

      void dispatch(askAlert(message));
    },
    [clearTimer, dispatch]
  );

  /**
   * 재생 상태 보고. `playing=false` 면 서버가 종료로 보고 적립 여부를 판정한다.
   */
  const report = useCallback(
    async (isPlaying: boolean, reason: string | null = null, keepalive = false) => {
      const reward = rewardRef.current;
      if (!reward) return;

      try {
        const response = await reportPlayback(
          {
            rewardSeq: reward.rewardSeq,
            campaignSeq: reward.campaignSeq,
            userSeq: reward.userSeq,
            sessionid: window.sessionid,
            current: playerRef.current?.getCurrentTime() ?? 0,
            duration: playerRef.current?.getDuration() ?? 0,
            playing: isPlaying,
            reason,
          },
          keepalive
        );

        if (!response.result) {
          /*
            ★ 서버가 **판정을 끝낸 실패**(`data === true`)면 이 재생은 죽은 것이다.
              다른 창에서 시작해 끊겼거나, 이미 처리된 경우다.

              경고만 띄우고 두면 소리가 계속 나서 **적립되지 않는 줄 모르고 끝까지
              듣게 된다.** 그래서 재생도 함께 멈춘다.

              `data` 가 true 가 아니면 잠깐의 네트워크 문제다. 서버 검증이 허용
              범위로 흡수하므로 조용히 넘어간다.
          */
          if (response.data === true) {
            abort(response.message ?? MSG.apiFail);
          }
          return;
        }

        const data = response.data as PlaybackResult | undefined;
        if (!data) return;

        rewardRef.current = data.reward;
        // 기준 시간을 채우면 버튼이 열린다. 재생이 끝나기 전에 열릴 수도 있다.
        setClaimable(!!data.claimable);

        /*
          ★ 보고 응답으로는 **적립되지 않는다.** (2026-09-16)
            재생이 끝나도 여기서는 축하하지 않는다 — 회원이 '적립 받기' 를 누르면
            그때 `handleClaim()` 이 결과를 알린다.

            다 듣고도 버튼이 열리지 않은 경우에만 서버가 이유를 담아 보낸다.
        */
        if (data.ended && !data.claimable && response.message) {
          await dispatch(askAlert(response.message));
        }
      } catch {
        // 네트워크 오류로 중간 보고가 빠지는 것은 서버 검증이 허용 범위로 흡수한다.
      }
    },
    [dispatch, abort]
  );

  /** 재생 시작 — 시작 보고 + 5초 주기 보고 */
  const start = useCallback(() => {
    if (!rewardRef.current || playingRef.current) return;

    setPlaying(true);
    reportedAtRef.current = playerRef.current?.getCurrentTime() ?? 0;
    void report(true);

    clearTimer();
    timerRef.current = window.setInterval(() => {
      const now = playerRef.current?.getCurrentTime() ?? 0;
      setCurrent(now);
      setDuration(playerRef.current?.getDuration() ?? 0);

      /*
        ★ **재생 위치가 5초 나아갔을 때만** 보고한다.

        예전에는 1초마다 깨어나 `Math.floor(now) % 5 === 0` 인지 봤다. 그러면
        일시정지했을 때 위치가 5의 배수에 멈춰 있으면 **매초 보고가 나가고**,
        그렇지 않으면 아예 안 나갔다. 멈춰 두고 시간만 흘려도 보고 횟수와
        경과시간이 쌓여 적립 판정을 통과할 수 있었다.

        위치를 기준으로 삼으면 멈춘 동안에는 아무것도 보내지 않는다.
        (서버 주석이 처음부터 전제하던 동작이다)
      */
      // 이미 적립받았거나 끝난 재생이면 더 보고하지 않는다. 진행 표시만 계속 움직인다.
      if (finishedRef.current) return;

      if (now >= reportedAtRef.current + REPORT_INTERVAL_SEC) {
        reportedAtRef.current = Math.floor(now / REPORT_INTERVAL_SEC) * REPORT_INTERVAL_SEC;
        void report(true);
      }
    }, 1000);
  }, [report, clearTimer]);

  /**
   * 적립 받기.
   *
   * 서버가 보고 기록으로 **다시 판정하고** 통과하면 포인트를 넣는다. 화면이 버튼을
   * 열어 줬다고 해서 지급되지는 않는다 — 판정은 서버 한 곳에서만 한다.
   */
  const handleClaim = useCallback(async () => {
    const reward = rewardRef.current;
    if (!reward || claimed || claiming) return;

    setClaiming(true);
    try {
      const response = await claimReward({
        rewardSeq: reward.rewardSeq,
        campaignSeq: reward.campaignSeq,
        userSeq: reward.userSeq,
        sessionid: window.sessionid,
        current: playerRef.current?.getCurrentTime() ?? 0,
        duration: playerRef.current?.getDuration() ?? 0,
        playing: false,
      });

      const data = response.data as PlaybackResult | undefined;
      if (!response.result || !data?.saved) {
        // 못 받았다. 버튼을 닫고 이유를 알린다(재생은 그대로 둔다).
        setClaimable(false);
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      /*
        받았다. 이제부터는 아무것도 보고하지 않는다 — 화면을 떠날 때의 취소 보고가
        **이미 지급된 행을 건드리지 않게** `finishedRef` 를 세운다.
      */
      finishedRef.current = true;
      setClaimed(true);
      setClaimable(false);
      await dispatch(askCustom('reward.save', String(data.amount)));
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setClaiming(false);
    }
  }, [claimed, claiming, dispatch]);

  /** 재생 종료/중단 — 서버에 끝났다고 알린다(적립은 버튼으로) */
  const finish = useCallback(
    (reason: string | null = null) => {
      if (finishedRef.current) return;
      finishedRef.current = true;

      setPlaying(false);
      clearTimer();
      void report(false, reason);
    },
    [report, clearTimer]
  );

  /*
    앱에 재생 상태를 알린다. (2026-10-08)

    앱은 재생 중인 동안 알림을 띄워 두어, 앱을 내려도 안드로이드가 웹뷰를 정리하지
    않게 한다. 알림의 '일시정지' 를 누르면 `mediaControl` 이 돌아온다.
    일반 브라우저에서는 아무 일도 없다.
  */
  function tellApp(state: number) {
    // 유튜브와 같은 숫자 — 1 재생 / 2 일시정지 / 0 종료. 버퍼링 등은 건너뛴다.
    const name = state === 1 ? 'playing' : state === 2 ? 'paused' : state === 0 ? 'stopped' : null;
    if (!name) return;

    appNotify('media', {
      state: name,
      title: campaign?.openTitle || campaign?.title || '',
      artist: campaign?.song?.artistname ?? '',
    });
  }

  useEffect(() => {
    const off = onAppEvent('mediaControl', (event) => {
      if (event.action === 'pause') playerRef.current?.pause();
    });
    return () => {
      off();
      appNotify('media', { state: 'stopped' });
    };
  }, []);

  // 비로그인이면 로그인 화면으로
  useEffect(() => {
    if (!hydrated) return;
    if (!user?.id) route(`/login?r=${encodeURIComponent(returnUrl())}`, true);
  }, [hydrated, user?.id]);

  // 캠페인 + 리워드 준비
  useEffect(() => {
    if (!user?.userSeq || !id) return;

    let cancelled = false;

    void (async () => {
      const leave = async (message: string) => {
        if (cancelled) return;
        await dispatch(askAlert(message));
        route(backTo, true);
      };

      try {
        const response = await prepareCampaign(user.userSeq, id, window.sessionid);
        if (cancelled) return;

        if (!response.result) return leave(response.message ?? MSG.apiFail);

        const next = response.data?.campaign;
        if (!next) return leave(MSG.apiFail);

        // ★ `available` 로 막지 않는다. 이 값은 '재생해도 되는가' 가 아니라
        //   **'배수가 붙는가'** 다(`ModuleCampaign::availableBenefit()`).
        //   기본 리워드(`F_REWARD_TYPE='D'`)는 **항상 false** 라서, 여기서 막으면
        //   기본 미션은 아무도 못 연다. 서버도 `available ? 금액×배수 : 금액` 으로
        //   기본 금액은 그대로 지급한다 — 막을 이유가 없다.
        //
        //   실제로 들어올 수 없는 미션(비공개·기간 밖·없는 번호)은 서버가
        //   `prepare` 단계에서 걸러 message 로 알려 준다.
        if (!next.availableToday) {
          return leave(`이미 이 미션의 보상을 받았어요.\n${retryText(response.data?.nextRewardAt)}`);
        }

        const reward = response.data?.reward;
        if (!reward?.rewardSeq) {
          return leave('리워드 정보가 없습니다.\n페이지를 새로고침 해주세요.');
        }

        // 재생할 것이 없으면 들어오지 않는다.
        //   영상 미션 — 유튜브 주소가 있어야 한다
        //   음악 미션 — 유튜브 주소나 mp3 중 하나
        if (playsYoutube(next)) {
          if (!extractYoutubeId(next.youtubeUrl)) return leave('재생할 영상이 없는 미션입니다.');
        } else if (!next.song?.hasAudio) {
          return leave('음원이 준비되지 않은 미션입니다.');
        }

        /*
           예산이 바닥난 미션이면 **듣기 전에** 뜻을 묻는다.

           ★ 재생이 시작된 뒤에 물으면 팝업 뒤로 소리가 흐르고, 나가기를 골랐을 때
             취소 보고가 한 번 더 돌아 "취소되었습니다" 팝업이 이어 뜬다.
             아직 아무것도 시작하지 않은 여기서 물으면 그냥 돌아가면 된다.
         */
        if (response.data?.soldOut) {
          const go = await dispatch(
            askConfirm('모든 리워드가 소진되었습니다.\n그래도 재생하시겠습니까?')
          );
          if (cancelled) return;

          if (!go) {
            route(backTo, true);
            return;
          }
          setSoldOut(true);
        }

        setCampaign(next);
        setAudioTicket(response.data?.audioTicket ?? null);
        setVideoId(extractYoutubeId(next.youtubeUrl) ?? undefined);
        rewardRef.current = reward;
      } catch {
        return leave(MSG.apiFail);
      }
    })();

    return () => {
      cancelled = true;
    };
    // backTo 는 최초 진입 시점의 값으로 고정한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userSeq, id, dispatch]);

  // 새로고침·창닫기로 이탈하면 적립을 취소한다.
  useEffect(() => {
    /*
      🔴 **여기서 취소를 보내면 안 된다.**

      예전에는 `beforeunload` 에서 바로 `finish()` 를 불렀다. 그런데 이 시점은
      **아직 사용자가 답하기 전**이다. "머무르기" 를 눌러도 취소는 이미 서버에
      도착해 있었고, 기록에는 '사용자 새로고침으로 인한 취소' 로 남았다.
      브라우저는 무엇을 눌렀는지 알려주지 않으므로, 여기서는 **묻기만** 한다.
    */
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!playingRef.current || finishedRef.current) return;
      e.preventDefault();
    };

    /*
      실제로 떠날 때만 취소를 보낸다.

      `pagehide` 는 창을 닫거나 다른 주소로 옮길 때 온다. `persisted` 가 true 면
      뒤로가기 캐시로 잠깐 치워두는 것이라 돌아올 수 있으므로 건드리지 않는다.

      언로드 중에는 보통의 요청이 끊기므로 `keepalive` 로 보낸다.
    */
    const onPageHide = (e: PageTransitionEvent) => {
      if (e.persisted) return;
      if (!playingRef.current || finishedRef.current) return;

      finishedRef.current = true;
      void report(false, '사용자 이탈로 인한 취소', true);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [report]);

  // 화면을 벗어날 때 정리.
  // 브라우저 뒤로가기처럼 확인창을 띄울 수 없는 경로로 빠져나가도, 적립을
  // 취소해 대기 상태('H') 리워드가 남지 않게 한다. 언마운트 후이므로 상태는
  // 건드리지 않고 서버 보고만 한다.
  useEffect(
    () => () => {
      clearTimer();
      if (playingRef.current && !finishedRef.current) {
        finishedRef.current = true;
        void report(false, '페이지 이동으로 인한 취소');
      }
    },
    [clearTimer, report]
  );

  /**
   * 이 화면을 떠나도 되는지 확인한다.
   * 재생 중이면 한 번 묻고, 나가기로 하면 적립을 취소한다.
   */
  async function confirmLeave(): Promise<boolean> {
    if (!playingRef.current) return true;
    if (!(await dispatch(askCustom('leave.video')))) return false;

    finish('페이지 이동으로 인한 취소');
    return true;
  }

  /** 헤더 뒤로가기 */
  async function handleBack() {
    if (await confirmLeave()) route(backTo);
  }

  // 탭바·헤더 아이콘 등 화면 안의 모든 내부 링크에 같은 확인을 건다.
  useLeaveConfirm(pageRef, playing, confirmLeave, (href) => route(href));

  /**
   * 화면에 띄울 지급액.
   *
   * 목록(`미션`)과 **같은 함수**를 쓴다. 각자 계산하다 목록 100P / 재생 50P 로
   * 어긋난 적이 있다.
   */
  const rewardAmount = campaign ? missionReward(campaign) : 0;

  /** 배수가 붙는 동안만 보여 줄 표시 */
  const boosted = !!campaign && missionBoosted(campaign);
  /** 기간 베네핏이면 언제까지인지. 사용자 베네핏(건수)에는 마감일이 없다. */
  const boostUntil = campaign ? benefitUntil(campaign) : '';

  const remain = Math.max(0, Math.round(duration - current));
  const progress = playing && duration > 0 ? Math.min(100, (current / duration) * 100) : 0;

  return (
    <div class="page" ref={pageRef}>
      <header class="play">
        <NavBack onBack={handleBack} />
        {/* 이동 확인은 useLeaveConfirm 이 캡처 단계에서 일괄 처리한다 */}
        <a href="/mypage">
          <div class="nav-icon">
            <img src="/images/user.png" alt="마이페이지" />
          </div>
        </a>
      </header>

      <div class="section">
        <div class="album-card">
          <div class="album-info">
            <div class="album-title">{campaign?.openTitle || campaign?.title || ''}</div>
            {campaign?.song?.artistname && <div class="album-sub">{campaign.song.artistname}</div>}
          </div>
        </div>

        <div class="video-wrap">
          {campaign && !playsYoutube(campaign) ? (
            <AudioPlayer
              campaignSeq={campaign.seq}
              userSeq={user?.userSeq}
              sessionid={window.sessionid}
              ticket={audioTicket}
              cover={campaignThumbnail(campaign, '')}
              callbacks={{
                onReady: (player) => {
                  playerRef.current = player;
                  setDuration(player.getDuration());
                },
                onStateChange: (state) => {
                  tellApp(state);
                  // 유튜브와 같은 숫자를 쓴다 — 1 재생 / 0 종료
                  if (state === 1) start();
                  else if (state === 0) finish();
                },
                onError: (message) => {
                  /*
                    ★ **이미 재생 중이면 취소하지 않는다.**

                    음원은 이미 받아서 소리가 나고 있는데, 뒤늦게 도착한 내려받기
                    오류로 적립을 취소해 버리는 일이 있었다. 진짜로 재생이 끊기면
                    화면을 떠날 때 정리된다.
                  */
                  if (playingRef.current) return;
                  finish(message);
                },
              }}
            />
          ) : (
            <YouTubePlayer
              videoId={videoId}
              callbacks={{
                onReady: (player) => {
                  // 유튜브는 `pauseVideo()` 라 이름이 다르다. 같은 모양으로 감싼다.
                  playerRef.current = {
                    getCurrentTime: () => player.getCurrentTime?.() ?? 0,
                    getDuration: () => player.getDuration?.() ?? 0,
                    pause: () => player.pauseVideo?.(),
                  };
                  setDuration(player.getDuration?.() ?? 0);
                },
                onStateChange: (state) => {
                  tellApp(state);
                  // 1 PLAYING / 0 ENDED
                  if (state === 1) start();
                  else if (state === 0) finish();
                },
                onError: (code) => finish(YOUTUBE_ERROR[code] ?? '알 수 없는 유튜브 오류'),
              }}
            />
          )}
        </div>

        <div class={soldOut ? 'reward-box reward-box--off' : 'reward-box'}>
          <div class="progress-bar">
            <div class="progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div class="reward-section">
            <div class="reward-left">
              <div class="reward-time">
                {playing ? (
                  <>
                    <span>{formatMinSec(remain)}</span> 남았어요
                  </>
                ) : claimed ? (
                  '적립이 완료되었어요'
                ) : soldOut ? (
                  '리워드가 모두 소진되었어요'
                ) : claimable ? (
                  '적립 받기를 누르세요'
                ) : (
                  '재생 버튼을 눌러 시작하세요'
                )}
              </div>
              <div class="reward-meta">
                {campaign ? missionRepeat(campaign) : ''}
                {boosted && boostUntil ? ` · ${boostUntil}까지 ${campaign?.rewardMultiple}배` : ''}
              </div>
            </div>

            <div class="reward-right">
              {boosted && <span class="reward-boost">×{campaign?.rewardMultiple}</span>}
              <span class="reward-coin">
                <img src="/images/point.png" alt="" class="reward-coin-img" />
              </span>
              <span class="reward-amount">{rewardAmount}</span>
            </div>
          </div>

          {/*
            시안의 '적립받기' 버튼. (2026-09-16) 이제 **실제로 누르는 버튼**이다 —
            기준 시간을 채우면 열리고, 눌러야 적립된다. 서버가 같은 검증으로 다시 판정한다.
          */}
          <button
            type="button"
            class="reward-claim-btn"
            disabled={!claimable || claimed || claiming || soldOut}
            onClick={() => void handleClaim()}
          >
            {soldOut
              ? '리워드 소진 · 적립되지 않아요'
              : claimed
                ? '적립 완료'
                : claiming
                  ? '적립 중…'
                  : claimable
                    ? '적립 받기'
                    : '끝까지 시청하면 적립받을 수 있어요'}
          </button>

          {/*
            ★ **받기 전에 나가면 처음부터 다시 봐야 한다는 것을 미리 알린다.** (2026-09-16)

            `prepare` 가 대기 중인 리워드 행의 시청 기록(`F_ETC`·`F_STARTDATE`)을
            초기화하므로, 다 보고 나서 새로고침하거나 다른 미션을 틀면 그동안의
            재생이 사라진다. 이 동작은 **일부러 그대로 둔다** — 다시 보게 되는 것이
            조회수로 이어지기 때문이다. 대신 사용자가 모르고 시간을 잃지 않도록
            버튼 바로 아래에서 말해 준다.
          */}
          {!claimed && !soldOut && (
            <p class="reward-claim-note">
              적립 받기를 누르기 전에 새로고침하거나 다른 미션을 재생하면 처음부터 다시
              재생해야 합니다.
            </p>
          )}
        </div>

        <div class="content">
          <div class="desc-section">
            {/*
              ★ **비고는 안내문을 대신하는 글이 아니다.**

              예전에는 `비고 || 안내문` 이라 관리 화면에 비고를 적는 순간 안내문이
              통째로 사라졌다. 실제 비고는 전부 `Snow x Love (feat. 나현&청산) -
              Elbbit` 같은 **곡 소개**라, 미션 12건 중 11건에서 재참여 주기도
              중도 이탈 안내도 아예 보이지 않았다.

              둘은 성격이 다르므로 따로 그린다.
            */}
            {campaign?.bigo && <p class="desc-text desc-text--bigo">{campaign.bigo}</p>}

            <p class="desc-text">
              콘텐츠를 끝까지 감상한 뒤 포인트를 받을 수 있습니다.
              <br />
              {/* 주기는 미션마다 다르다(`F_INIT_TIME`). 0 이면 자정 기준 하루 한 번 */}
              중도 이탈 시 포인트가 지급되지 않으며, 동일 미션은{' '}
              {campaign ? missionRepeat(campaign) : '재참여 가능 시간 이후'} 다시 참여할 수
              있습니다.
              <br />
              적립 받기를 누르기 전에 새로고침하거나 다른 기기·창에서 다른 미션을 재생하면
              시청 기록이 초기화되어 처음부터 다시 재생해야 합니다.
              <br />
              비정상적인 반복 재생이나 자동화 참여가 확인되면 적립이 취소될 수 있습니다.
            </p>
          </div>

          {campaign?.youtubeUrl && (
            <a
              href={campaign.youtubeUrl}
              class="youtube-circle-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="유튜브에서 보기"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28.57 20">
                <g>
                  <path
                    d="M27.9727 3.12324C27.6435 1.89323 26.6768 0.926623 25.4468 0.597366C23.2197 2.24288e-07 14.285 0 14.285 0C14.285 0 5.35042 2.24288e-07 3.12323 0.597366C1.89323 0.926623 0.926623 1.89323 0.597366 3.12324C2.24288e-07 5.35042 0 10 0 10C0 10 2.24288e-07 14.6496 0.597366 16.8768C0.926623 18.1068 1.89323 19.0734 3.12323 19.4026C5.35042 20 14.285 20 14.285 20C14.285 20 23.2197 20 25.4468 19.4026C26.6768 19.0734 27.6435 18.1068 27.9727 16.8768C28.5701 14.6496 28.5701 10 28.5701 10C28.5701 10 28.5677 5.35042 27.9727 3.12324Z"
                    fill="#FF0000"
                  />
                  <path
                    d="M11.4253 14.2854L18.8477 10.0004L11.4253 5.71533V14.2854Z"
                    fill="white"
                  />
                </g>
              </svg>
            </a>
          )}
        </div>
      </div>

      <TabBar path="/play" />
    </div>
  );
};

export default Play;
